/**
 * What a response from this app's API means.
 *
 * Separate from `api.ts` on purpose. `api.ts` builds the authenticated client
 * and therefore imports `../firebase`; every page module's test mocks `./api`
 * with a factory that REPLACES the module, so anything living there is
 * `undefined` inside a test. A pure helper with no dependencies belongs
 * somewhere the tests do not have to mock at all.
 */

/**
 * The JSON body of a response, or an Error carrying the server's own message.
 *
 * Each of the five page modules had its own copy of this, and they had already
 * drifted: only the Cards copy handled 204, so an endpoint returning no
 * content would have thrown a JSON parse error from the other four.
 *
 * `detail` is FastAPI's error shape, and surfacing it beats a bare status: the
 * server writes messages like "POINTS_ENCRYPTION_KEYS is malformed", which
 * tells the reader the data is intact and the key is not.
 */
export async function orThrow(
  res: Response,
  fallback: string,
): Promise<unknown> {
  if (res.ok) return res.status === 204 ? null : await res.json();
  let detail: string | undefined;
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (typeof body.detail === "string") detail = body.detail;
  } catch {
    // Non-JSON error body. Fall through to the status-code message rather than
    // masking the original failure with a parse error.
  }
  throw new Error(detail ?? `${fallback} (HTTP ${res.status})`);
}
